
import type { Application } from '@/types/database.types';

/**
 * Helper function to safely convert values to boolean
 */
const toBoolean = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
};

/**
 * Helper function to safely convert values to number
 */
const toNumber = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  return 0;
};

/**
 * Checks if a section has all its required fields filled
 * @param section The section name to check
 * @param data The application data
 * @returns boolean indicating if all required fields are present
 */
export const hasRequiredFields = (section: string, data?: Partial<Application>): boolean => {
  if (!data) return false;
  
  switch (section) {
    case 'Basic Information':
      return !!(data.first_name && data.last_name && data.email && data.phone);
    case 'Entity Details':
      // Add handling for both old and new corporate structure formats
      const hasValidCorporateStructure = !!data.corporate_structure;
      return !!(data.company_legal_name || data.company_dba) && !!data.naics_code && hasValidCorporateStructure;
    case 'Insurance Details':
      // Simplified validation - mark complete if ANY carrier field has meaningful content
      const hasCurrentMedical = !!(data.current_medical_carrier && data.current_medical_carrier.trim());
      const hasCurrentWorkers = !!(data.current_workers_comp_carrier && data.current_workers_comp_carrier.trim());
      const hasCurrentAncillary = !!(data.current_ancillary_carrier && data.current_ancillary_carrier.trim());
      
      // Check desired carriers - must have actual content
      const hasDesiredMedical = Array.isArray(data.desired_medical_carriers) && 
                               data.desired_medical_carriers.length > 0 && 
                               data.desired_medical_carriers.some(carrier => carrier && carrier.trim());
                               
      const hasDesiredAncillary = Array.isArray(data.desired_ancillary_carriers) && 
                                 data.desired_ancillary_carriers.length > 0 && 
                                 data.desired_ancillary_carriers.some(carrier => carrier && carrier.trim());
      
      // Mark section as complete if user has filled any meaningful carrier information
      const isComplete = hasCurrentMedical || hasCurrentWorkers || hasCurrentAncillary || 
                        hasDesiredMedical || hasDesiredAncillary;
      
      console.log("Insurance validation result:", { 
        hasCurrentMedical, 
        hasCurrentWorkers, 
        hasCurrentAncillary,
        hasDesiredMedical,
        hasDesiredAncillary,
        isComplete,
        values: {
          current_medical_carrier: data.current_medical_carrier,
          current_workers_comp_carrier: data.current_workers_comp_carrier,
          current_ancillary_carrier: data.current_ancillary_carrier,
          desired_medical_carriers: data.desired_medical_carriers,
          desired_ancillary_carriers: data.desired_ancillary_carriers
        }
      });
      
      return isComplete;
      
    case 'Staffing & Payroll':
    case 'Payroll Schedule': // Added this case to handle both naming conventions
      // Only mark complete if actual data has been entered
      const hasPayScheduleType = !!data.pay_schedule_type;
      const hasNextCheckDate = !!data.next_check_date;
      
      // Log the payroll data for debugging
      console.log("Payroll Schedule validation data:", {
        pay_schedule_type: data.pay_schedule_type,
        pay_period_start: data.pay_period_start,
        pay_period_end: data.pay_period_end,
        next_check_date: data.next_check_date,
        has_bonuses: data.has_bonuses,
        has_commission: data.has_commission
      });
      
      // Require both pay schedule type and next check date
      return hasPayScheduleType && hasNextCheckDate;
    
    case 'Operational Details':
    case 'Operations & Risk Assessment': // Handle both naming conventions
      // Check for business activities and basic risk assessment completion
      const hasBusinessActivities = !!(data.business_activities && data.business_activities.trim());
      
      // Check if any risk assessment questions have been answered with TRUE values (user interaction)
      const hasRiskAssessmentData = 
             data.has_over_road_trucking === true ||
             data.has_cannabis_hemp_contact === true ||
             data.has_elevated_workplace_risk === true ||
             data.has_company_aircraft === true ||
             data.has_marine_operations === true ||
             data.has_heavy_machinery === true ||
             data.has_recent_wc_claims === true ||
             data.has_safety_program === true ||
             data.has_recent_layoffs === true ||
             data.has_planned_layoffs === true ||
             data.has_foreign_travel === true ||
             data.has_epli_insurance === true;
      
      // Check for meaningful text content in detail fields
      const hasDetailContent = !!(
        (data.cannabis_hemp_details && data.cannabis_hemp_details.trim()) ||
        (data.workplace_risk_details && data.workplace_risk_details.trim()) ||
        (data.safety_program_details && data.safety_program_details.trim()) ||
        (data.recent_layoffs_details && data.recent_layoffs_details.trim()) ||
        (data.planned_layoffs_details && data.planned_layoffs_details.trim()) ||
        (data.foreign_travel_details && data.foreign_travel_details.trim()) ||
        (data.epli_insurance_details && data.epli_insurance_details.trim())
      );
      
      // Check for carrier information completion
      const hasCarrierInfo = !!(data.current_workers_comp_carrier && data.current_workers_comp_carrier.trim());
      
      // Check for company service descriptions
      const hasServiceDescription = !!(data.company_services_description && data.company_services_description.trim());
      const hasPhysicalDescription = !!(data.physical_operations_description && data.physical_operations_description.trim());
      
      const isOperationalComplete = hasBusinessActivities || hasRiskAssessmentData || hasDetailContent || 
                                   hasCarrierInfo || hasServiceDescription || hasPhysicalDescription;
      
      console.log("Operational Details validation:", {
        hasBusinessActivities,
        hasRiskAssessmentData,
        hasDetailContent,
        hasCarrierInfo,
        hasServiceDescription,
        hasPhysicalDescription,
        isComplete: isOperationalComplete,
        business_activities: data.business_activities,
        risk_flags: {
          has_over_road_trucking: data.has_over_road_trucking,
          has_cannabis_hemp_contact: data.has_cannabis_hemp_contact,
          has_elevated_workplace_risk: data.has_elevated_workplace_risk,
          has_company_aircraft: data.has_company_aircraft,
          has_marine_operations: data.has_marine_operations,
          has_heavy_machinery: data.has_heavy_machinery,
          has_recent_wc_claims: data.has_recent_wc_claims,
          has_safety_program: data.has_safety_program
        }
      });
      
      return isOperationalComplete;
      
    // Add dedicated validation for Medical Questionnaire section
    case 'Medical Questionnaire':
      // Check if any of the medical condition fields have been set (not just undefined)
      // and require at least one field to be explicitly set (true or false)
      const hasMedicalCondition = 
             data.has_kidney_disease === true ||
             data.has_diabetes === true ||
             data.has_liver_disease === true ||
             data.has_neural_disorder === true ||
             data.has_cardiovascular === true ||
             data.has_respiratory_disease === true ||
             data.has_musculoskeletal === true ||
             data.has_transplants === true ||
             data.has_cancer === true ||
             data.has_hiv_aids === true ||
             data.has_mental_health === true ||
             data.has_nervous_system === true ||
             data.has_other_conditions === true ||
             data.has_premature_birth === true;
      
      const hasNoMedicalCondition = 
             data.has_kidney_disease === false ||
             data.has_diabetes === false ||
             data.has_liver_disease === false ||
             data.has_neural_disorder === false ||
             data.has_cardiovascular === false ||
             data.has_respiratory_disease === false ||
             data.has_musculoskeletal === false ||
             data.has_transplants === false ||
             data.has_cancer === false ||
             data.has_hiv_aids === false ||
             data.has_mental_health === false ||
             data.has_nervous_system === false ||
             data.has_other_conditions === false ||
             data.has_premature_birth === false;
             
      // Section is complete if at least one condition has been explicitly set
      return hasMedicalCondition || hasNoMedicalCondition;
             
    // Add Workers Comp Assessment validation
    case 'Workers Comp Assessment':
      // Consider the section complete if any of these key fields have been explicitly set
      return data.has_over_road_trucking === true ||
             data.has_over_road_trucking === false ||
             data.has_cannabis_hemp_contact === true ||
             data.has_cannabis_hemp_contact === false ||
             data.has_elevated_workplace_risk === true ||
             data.has_elevated_workplace_risk === false ||
             data.has_company_aircraft === true ||
             data.has_company_aircraft === false ||
             data.has_marine_operations === true ||
             data.has_marine_operations === false ||
             data.has_heavy_machinery === true ||
             data.has_heavy_machinery === false ||
             data.has_recent_wc_claims === true ||
             data.has_recent_wc_claims === false;
             
    case 'Document Upload':
      // Check if ANY of the required document files have been uploaded
      // We only need at least one document to mark this section as complete
      console.log("Document upload validation - checking files:", {
        employee_census: data.employee_census_file,
        dependent_census: data.dependent_census_file,
        workers_comp_declaration: data.workers_comp_declaration_file,
        health_insurance_renewal: data.health_insurance_renewal_file,
        other_documents: data.other_documents
      });
      
      // Check for actual file uploads, not just the existence of an empty array
      // Use type assertion to ensure TypeScript recognizes it as an array
      const hasOtherDocuments = Array.isArray(data.other_documents) && 
                              (data.other_documents as any[]).length > 0;
      
      // If other_documents is a string (serialized JSON), check if it contains actual documents
      // Fix: Add type assertion to avoid the "length" property on "never" type error
      const hasStringOtherDocuments = typeof data.other_documents === 'string' && 
                                    data.other_documents !== '[]' && 
                                    (data.other_documents as string).length > 2; // More than just '[]'
      
      return !!(
        data.employee_census_file || 
        data.dependent_census_file || 
        data.workers_comp_declaration_file || 
        data.workers_comp_loss_runs_file ||
        data.health_insurance_renewal_file ||
        data.tax_exempt_status_file ||
        hasOtherDocuments ||
        hasStringOtherDocuments
      );

    case 'PEO Preferences':
      // FIX: Only mark as complete if needs_guidance has been explicitly set to true or false AND
      // the user has explicitly interacted with the PEO preferences section
      
      // First check if needs_guidance is explicitly set (not null)
      const hasSetGuidance = data.needs_guidance === true || data.needs_guidance === false;
      
      // Then check for user interaction with PEO preferences
      // Fix: Use type assertion to ensure TypeScript recognizes these as arrays
      const hasSelectedNeeds = Array.isArray(data.top_3_peo_needs) && 
                             (data.top_3_peo_needs as any[]).length > 0;
                             
      const hasSelectedPreferred = Array.isArray(data.preferred_peos) && 
                                 (data.preferred_peos as any[]).length > 0;
                                 
      const hasSelectedExcluded = Array.isArray(data.excluded_peos) && 
                                (data.excluded_peos as any[]).length > 0;
      
      // Add logging to debug
      console.log("PEO Preferences validation:", {
        needs_guidance: data.needs_guidance,
        hasSetGuidance,
        hasSelectedNeeds,
        hasSelectedPreferred,
        hasSelectedExcluded,
        top_3_peo_needs: data.top_3_peo_needs,
        preferred_peos: data.preferred_peos,
        excluded_peos: data.excluded_peos
      });
      
      // Only consider complete if user has explicitly set needs_guidance AND
      // has selected some PEO needs OR preferred/excluded PEOs
      return hasSetGuidance && (hasSelectedNeeds || hasSelectedPreferred || hasSelectedExcluded);
      
    default:
      return false;
  }
};
